import { useInfiniteQuery } from "@tanstack/react-query";
import api from "./api";

const DOCUMENTS_PAGE_SIZE = 50;

// One server-paginated page of a customer's invoices + credit notes (the `isCreditNote` flag splits the
// two), newest first. `kind` filters server-side ("Invoice" | "CreditNote" | undefined = all); `search`
// matches the number or customer reference. Returns { total, page, pageSize, items }.
async function fetchCustomerDocumentInvoices(accountCode, { page, kind, search }, { signal } = {}) {
  const { data } = await api.get(`api/customers/${accountCode}/documents/invoices`, {
    params: {
      page,
      pageSize: DOCUMENTS_PAGE_SIZE,
      kind: kind || undefined,
      search: search?.trim() || undefined,
    },
    signal,
  });
  return data;
}

// Infinite-scroll list of a customer's documents. An account can carry thousands of invoices, so the
// list is paged, searched and kind-filtered on the server rather than loaded whole and sieved in the UI.
export function useCustomerDocumentInvoices(accountCode, { kind, search, enabled = true } = {}) {
  return useInfiniteQuery({
    queryKey: ["customerDocumentInvoices", accountCode, kind ?? "all", search?.trim() ?? ""],
    queryFn: ({ pageParam, signal }) =>
      fetchCustomerDocumentInvoices(accountCode, { page: pageParam, kind, search }, { signal }),
    enabled: enabled && !!accountCode,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.pageSize < lastPage.total ? lastPage.page + 1 : undefined,
    staleTime: 60_000,
  });
}

// Materialize one or more documents server-side; returns [{ documentType, reference, s3Key,
// previewUrl, fileName }]. documentType is "Invoice" (covers credit notes) or "Statement".
async function materializeDocuments(accountCode, body) {
  const { data } = await api.post(
    `api/customers/${accountCode}/documents/materialize`,
    body,
  );
  return data?.documents ?? [];
}

// Materialize a set of documents for the send/preview modal. Each spec is { documentType,
// invoiceNumbers?, salesOrderNumbers? }; the returned docs are enriched with their originating
// numbers so the send can carry timeline metadata. Specs are materialized in parallel and settle
// independently, so one that errors or comes back empty is reported in `failed` (indexes into
// `specs`) rather than sinking the rest. Returns { docs, failed }.
export async function prepareDocumentsForSend(accountCode, specs) {
  const settled = await Promise.allSettled(
    specs.map((spec) =>
      materializeDocuments(accountCode, {
        documentType: spec.documentType,
        invoiceNumbers: spec.invoiceNumbers,
        salesOrderNumbers: spec.salesOrderNumbers,
      }),
    ),
  );
  const docs = [];
  const failed = [];
  settled.forEach((result, i) => {
    const ready = result.status === "fulfilled" ? result.value.filter((d) => d.s3Key) : [];
    if (ready.length === 0) {
      failed.push(i);
      return;
    }
    for (const d of ready) {
      docs.push({
        ...d,
        invoiceNumbers: specs[i].invoiceNumbers ?? [],
        salesOrderNumbers: specs[i].salesOrderNumbers ?? [],
      });
    }
  });
  return { docs, failed };
}

// Download a set of documents (from { documentType, invoiceNumbers? } specs). Materializes them,
// then downloads: a single file directly, or several zipped into one so the browser does one
// download (avoiding Chrome's multiple-downloads prompt). Returns { count, failed } — how many
// were downloaded, and which specs (by index) couldn't be generated.
export async function downloadDocumentsSet(accountCode, specs, zipName = "documents.zip") {
  const { docs, failed } = await prepareDocumentsForSend(accountCode, specs);
  if (docs.length === 1) {
    await downloadByKey(docs[0].s3Key, docs[0].fileName ?? "document.pdf");
  } else if (docs.length > 1) {
    await downloadBundle(
      docs.map((d) => d.s3Key),
      zipName,
    );
  }
  return { count: docs.length, failed };
}

// Send selected documents to a customer by email (creates an email.send action server-side, which
// dispatches to messaging-service). The documents must already be materialized (attachmentKeys).
export async function sendDocumentEmail(customerId, payload) {
  await api.post(`api/customers/${customerId}/documents/send`, payload);
}

// Pull a document's bytes through the authenticated same-origin preview proxy (a bearer token can't
// ride on an <a>/<iframe> src) and save it to disk as `fileName`.
async function downloadByKey(s3Key, fileName) {
  const { data } = await api.get(`api/documents/preview`, {
    params: { key: s3Key },
    responseType: "blob",
    bypassInterceptor: true,
  });
  const url = URL.createObjectURL(data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || "document";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// Materialize then download an invoice / credit note / statement.
export async function downloadDocument(
  accountCode,
  { documentType, invoiceNumbers, salesOrderNumbers, fileName },
) {
  const docs = await materializeDocuments(accountCode, {
    documentType,
    invoiceNumbers,
    salesOrderNumbers,
  });
  const doc = docs[0];
  if (!doc?.s3Key) throw new Error("This document couldn't be generated — please try again.");
  await downloadByKey(doc.s3Key, doc.fileName ?? fileName);
}

// Fetch a server-zipped bundle of several documents (by S3 key) as ONE download — a page that fires
// more than one download in a row trips Chrome's "download multiple files?" permission prompt.
async function downloadBundle(keys, fileName) {
  const { data } = await api.post(
    `api/documents/bundle`,
    { keys, fileName },
    { responseType: "blob", bypassInterceptor: true },
  );
  const url = URL.createObjectURL(data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || "documents.zip";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// A proof of delivery must be materialized first (its ledger keyField is a source reference, not a
// ready S3 object) — same as the microFE's onSelectPod flow. One materialize call scoped to the one
// invoice (never fanned out across the ledger); returns how many were found. When an invoice has
// several PODs they're zipped into a single download so the browser doesn't block the extras.
export async function downloadProofOfDeliveries(accountCode, invoiceNumber) {
  const docs = await materializeDocuments(accountCode, {
    documentType: "ProofOfDelivery",
    invoiceNumbers: [invoiceNumber],
  });
  const ready = docs.filter((d) => d.s3Key);
  if (ready.length === 1) {
    await downloadByKey(ready[0].s3Key, ready[0].fileName ?? `POD-${invoiceNumber}.pdf`);
  } else if (ready.length > 1) {
    await downloadBundle(
      ready.map((d) => d.s3Key),
      `POD-${invoiceNumber}.zip`,
    );
  }
  return ready.length;
}
